import {
  CRYPTO_FEE_BILLABLE_EVENTS,
  type CompanyCryptoFeeOverrideApi,
  type CryptoFeeBillableEvent,
  type CryptoFeeTariff,
  type CryptoFeeTariffConfig,
  type CryptoFeeWalletRef,
  type PatchCompanyCryptoFeeOverrideBody,
} from "../../../billing/components/crypto-wallet-fees/cryptoWalletFeeSettings.types";
import {
  createDefaultCryptoWalletPlatformDraft,
  CRYPTO_WALLET_ADMIN_PROVIDERS,
  CRYPTO_WALLET_PROVIDER_CROSSMINT,
  cryptoFeeEventLabel,
  sanitizePercentageInput,
  sanitizeStablecoinAmountInput,
  type CryptoFeeEventTariffDraft,
  type CryptoFeeTariffDraftValue,
  type CryptoWalletRefDraft,
} from "../../../billing/components/crypto-wallet-fees/cryptoWalletFeeSettingsForm";

export type CompanyCryptoEnabledMode = "inherit" | "enabled" | "disabled";
export type CompanyCryptoTariffMode = "inherit" | "custom";
export type CompanyCryptoWalletMode = "inherit" | "custom";

export type CompanyCryptoWalletFeeOverrideDraft = {
  walletProvider: string;
  enabledMode: CompanyCryptoEnabledMode;
  tariffMode: CompanyCryptoTariffMode;
  defaultTariffEnabled: boolean;
  defaultTariff: CryptoFeeTariffDraftValue;
  eventTariffs: Record<CryptoFeeBillableEvent, CryptoFeeEventTariffDraft>;
  walletSandboxMode: CompanyCryptoWalletMode;
  walletSandbox: CryptoWalletRefDraft;
  walletProductionMode: CompanyCryptoWalletMode;
  walletProduction: CryptoWalletRefDraft;
};

const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

function emptyWalletRefDraft(): CryptoWalletRefDraft {
  return {
    address: "",
    chain: "",
  };
}

function numberToInputValue(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "";
  return `${value}`;
}

function tariffToDraftValue(
  tariff: CryptoFeeTariff | null | undefined
): CryptoFeeTariffDraftValue {
  return {
    percentageFee: numberToInputValue(tariff?.percentageFee),
    flatFee: numberToInputValue(tariff?.flatFee),
  };
}

function hasCustomWalletValue(
  wallet: CryptoFeeWalletRef | null | undefined
): boolean {
  if (!wallet) return false;
  return !!wallet.address?.trim() && !!wallet.chain?.trim();
}

function walletToDraft(
  wallet: CryptoFeeWalletRef | null | undefined
): CryptoWalletRefDraft {
  return {
    address: wallet?.address ?? "",
    chain: wallet?.chain ?? "",
  };
}

function cloneEventTariffs(
  source: Record<CryptoFeeBillableEvent, CryptoFeeEventTariffDraft>
): Record<CryptoFeeBillableEvent, CryptoFeeEventTariffDraft> {
  return CRYPTO_FEE_BILLABLE_EVENTS.reduce((acc, event) => {
    acc[event] = { ...source[event] };
    return acc;
  }, {} as Record<CryptoFeeBillableEvent, CryptoFeeEventTariffDraft>);
}

export function createDefaultCompanyCryptoWalletFeeOverrideDraft(
  walletProvider: string = CRYPTO_WALLET_PROVIDER_CROSSMINT
): CompanyCryptoWalletFeeOverrideDraft {
  const base = createDefaultCryptoWalletPlatformDraft(walletProvider);
  return {
    walletProvider,
    enabledMode: "inherit",
    tariffMode: "inherit",
    defaultTariffEnabled: false,
    defaultTariff: { ...base.defaultTariff },
    eventTariffs: cloneEventTariffs(base.eventTariffs),
    walletSandboxMode: "inherit",
    walletSandbox: emptyWalletRefDraft(),
    walletProductionMode: "inherit",
    walletProduction: emptyWalletRefDraft(),
  };
}

export function companyCryptoWalletProviderLabel(
  walletProvider: string
): string {
  return (
    CRYPTO_WALLET_ADMIN_PROVIDERS.find((item) => item.value === walletProvider)
      ?.label ?? walletProvider
  );
}

function parseOptionalNonNegativeNumber(
  raw: string,
  label: string
): { value: number | null; error: string | null } {
  const normalized = sanitizeStablecoinAmountInput(raw).trim();
  if (!normalized) {
    return { value: null, error: null };
  }
  if (normalized === ".") {
    return { value: null, error: `${label} must be a valid number` };
  }
  const value = Number.parseFloat(normalized);
  if (!Number.isFinite(value)) {
    return { value: null, error: `${label} must be a valid number` };
  }
  if (value < 0) {
    return { value: null, error: `${label} must be zero or greater` };
  }
  return { value, error: null };
}

function parseOptionalPercentage(
  raw: string,
  label: string
): { value: number | null; error: string | null } {
  const normalized = sanitizePercentageInput(raw).trim();
  if (!normalized) {
    return { value: null, error: null };
  }
  if (normalized === ".") {
    return { value: null, error: `${label} must be a valid number` };
  }
  const value = Number.parseFloat(normalized);
  if (!Number.isFinite(value)) {
    return { value: null, error: `${label} must be a valid number` };
  }
  if (value < 0 || value > 100) {
    return { value: null, error: `${label} must be between 0 and 100` };
  }
  return { value, error: null };
}

function parseTariffValueDraft(
  labelPrefix: string,
  draft: CryptoFeeTariffDraftValue
): { value: CryptoFeeTariff; error: string | null } {
  const percentage = parseOptionalPercentage(
    draft.percentageFee,
    `${labelPrefix} percentage fee`
  );
  if (percentage.error) return { value: {}, error: percentage.error };

  const flatFee = parseOptionalNonNegativeNumber(
    draft.flatFee,
    `${labelPrefix} flat fee`
  );
  if (flatFee.error) return { value: {}, error: flatFee.error };

  return {
    value: {
      percentageFee: percentage.value,
      flatFee: flatFee.value,
    },
    error: null,
  };
}

function parseWalletRefDraft(
  label: string,
  draft: CryptoWalletRefDraft
): { value: CryptoFeeWalletRef | null; error: string | null } {
  const address = draft.address.trim();
  const chain = draft.chain.trim();
  if (!address || !chain) {
    return {
      value: null,
      error: `${label} wallet requires both address and chain`,
    };
  }
  if (!EVM_ADDRESS_REGEX.test(address)) {
    return {
      value: null,
      error: `${label} wallet address must be a valid EVM address`,
    };
  }
  return { value: { address, chain }, error: null };
}

export function companyCryptoWalletOverrideToDraft(
  override: CompanyCryptoFeeOverrideApi | null,
  walletProvider: string = CRYPTO_WALLET_PROVIDER_CROSSMINT
): CompanyCryptoWalletFeeOverrideDraft {
  const base = createDefaultCompanyCryptoWalletFeeOverrideDraft(walletProvider);
  if (!override) {
    return base;
  }

  const eventTariffs = cloneEventTariffs(base.eventTariffs);
  const byEvent = override.tariff?.byEvent;
  for (const event of CRYPTO_FEE_BILLABLE_EVENTS) {
    const eventTariff = byEvent?.[event];
    eventTariffs[event] = {
      enabled: eventTariff != null,
      ...tariffToDraftValue(eventTariff),
    };
  }

  const hasAnyEventOverride = CRYPTO_FEE_BILLABLE_EVENTS.some(
    (event) => eventTariffs[event].enabled
  );
  const hasDefaultOverride = override.tariff?.default != null;
  const hasTariffOverride = hasDefaultOverride || hasAnyEventOverride;

  return {
    walletProvider: override.walletProvider ?? walletProvider,
    enabledMode:
      override.enabled === true
        ? "enabled"
        : override.enabled === false
        ? "disabled"
        : "inherit",
    tariffMode: hasTariffOverride ? "custom" : "inherit",
    defaultTariffEnabled: hasDefaultOverride,
    defaultTariff: tariffToDraftValue(override.tariff?.default),
    eventTariffs,
    walletSandboxMode: hasCustomWalletValue(override.feeWalletSandbox)
      ? "custom"
      : "inherit",
    walletSandbox: walletToDraft(override.feeWalletSandbox),
    walletProductionMode: hasCustomWalletValue(override.feeWalletProduction)
      ? "custom"
      : "inherit",
    walletProduction: walletToDraft(override.feeWalletProduction),
  };
}

export function buildCompanyCryptoWalletFeeOverridePatchPayload(
  draft: CompanyCryptoWalletFeeOverrideDraft
): {
  payload: PatchCompanyCryptoFeeOverrideBody | null;
  error: string | null;
} {
  const payload: PatchCompanyCryptoFeeOverrideBody = {};

  if (draft.enabledMode === "inherit") {
    payload.inheritEnabled = true;
  } else {
    payload.enabled = draft.enabledMode === "enabled";
  }

  if (draft.tariffMode === "inherit") {
    payload.inheritTariff = true;
  } else {
    const tariff: CryptoFeeTariffConfig = {};
    let hasAnyTariffOverride = false;

    if (draft.defaultTariffEnabled) {
      const parsedDefault = parseTariffValueDraft(
        "Default",
        draft.defaultTariff
      );
      if (parsedDefault.error) {
        return { payload: null, error: parsedDefault.error };
      }
      tariff.default = parsedDefault.value;
      hasAnyTariffOverride = true;
    }

    const byEvent: Partial<
      Record<CryptoFeeBillableEvent, CryptoFeeTariff | null>
    > = {};
    // The editor shows payout and inbound_payment only. Stored legacy events stay in the payload.
    for (const event of CRYPTO_FEE_BILLABLE_EVENTS) {
      const eventDraft = draft.eventTariffs[event];
      if (!eventDraft.enabled) continue;
      const parsedEvent = parseTariffValueDraft(
        `${cryptoFeeEventLabel(event)} event`,
        eventDraft
      );
      if (parsedEvent.error) {
        return { payload: null, error: parsedEvent.error };
      }
      byEvent[event] = parsedEvent.value;
      hasAnyTariffOverride = true;
    }

    if (!hasAnyTariffOverride) {
      payload.inheritTariff = true;
    } else {
      if (Object.keys(byEvent).length > 0) {
        tariff.byEvent = byEvent;
      }
      payload.tariff = tariff;
    }
  }

  if (draft.walletSandboxMode === "inherit") {
    payload.inheritFeeWalletSandbox = true;
  } else {
    const parsedSandbox = parseWalletRefDraft("Sandbox", draft.walletSandbox);
    if (parsedSandbox.error || !parsedSandbox.value) {
      return { payload: null, error: parsedSandbox.error };
    }
    payload.feeWalletSandbox = parsedSandbox.value;
  }

  if (draft.walletProductionMode === "inherit") {
    payload.inheritFeeWalletProduction = true;
  } else {
    const parsedProduction = parseWalletRefDraft(
      "Production",
      draft.walletProduction
    );
    if (parsedProduction.error || !parsedProduction.value) {
      return { payload: null, error: parsedProduction.error };
    }
    payload.feeWalletProduction = parsedProduction.value;
  }

  return {
    payload,
    error: null,
  };
}
